import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, NewPasswordChallenge } from '../../services/auth.service';
import { LoginPayload } from '../login-card/login-card.component';
import { ForgotPasswordPayload } from '../forgot-password-card/forgot-password-card.component';
import { NewPasswordPayload } from '../new-password-card/new-password-card.component';
import { CognitoUser } from 'amazon-cognito-identity-js';

// The three views rendered inside the same login page
export type LoginView = 'login' | 'forgot-password' | 'new-password';

@Component({
  selector: 'app-login-page',
  standalone: false,
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.css'],
})
export class LoginPageComponent {
  currentView: LoginView = 'login';

  // Held between steps of the new-password challenge flow
  private pendingCognitoUser: CognitoUser | null = null;
  private pendingUserAttributes: any = null;
  pendingEmail = '';   // passed to new-password card so it can show who is logging in

  // Held between steps of the forgot-password flow
  forgotPasswordEmail = '';

  constructor(private authService: AuthService, private router: Router, private route: ActivatedRoute) {}

  // ── Login card events ─────────────────────────────────────────────────────
  async handleLogin(payload: LoginPayload): Promise<void> {
    try {
      const result = await this.authService.login(payload.email, payload.password);

      if ((result as NewPasswordChallenge).code === 'NewPasswordRequired') {
        // First-time login — switch to new-password view
        const challenge = result as NewPasswordChallenge;
        this.pendingCognitoUser    = challenge.cognitoUser;
        this.pendingUserAttributes = challenge.userAttributes;
        this.pendingEmail          = payload.email;
        this.currentView           = 'new-password';
      } else {
        // Normal login success
        this.navigateAfterLogin();
      }
    } catch (err: any) {
      payload.onError?.(err.message ?? 'Login failed. Please try again.');
    }
  }

  showForgotPassword(): void {
    this.currentView = 'forgot-password';
  }

  // ── Forgot-password card events ───────────────────────────────────────────
  async handleForgotPassword(payload: ForgotPasswordPayload): Promise<void> {
    if (payload.step === 'request') {
      try {
        await this.authService.forgotPassword(payload.email);
        this.forgotPasswordEmail = payload.email;
        payload.onSuccess?.('Code sent! Check your email.');
      } catch (err: any) {
        payload.onError?.(err.message);
      }
    }

    if (payload.step === 'confirm') {
      try {
        await this.authService.confirmForgotPassword(
          this.forgotPasswordEmail,
          payload.code!,
          payload.newPassword!
        );
        payload.onSuccess?.('Password updated! Please log in.');
        this.currentView = 'login';
      } catch (err: any) {
        payload.onError?.(err.message);
      }
    }
  }

  backToLogin(): void {
    this.currentView = 'login';
  }

  // ── New-password card events (first-time login) ───────────────────────────
  async handleNewPassword(payload: NewPasswordPayload): Promise<void> {
    if (!this.pendingCognitoUser) {
      payload.onError?.('Session expired. Please log in again.');
      this.currentView = 'login';
      return;
    }
    try {
      await this.authService.completeNewPassword(
        this.pendingCognitoUser,
        payload.newPassword,
        this.pendingUserAttributes
      );
      this.navigateAfterLogin();
    } catch (err: any) {
      payload.onError?.(err.message);
    }
  }

  private navigateAfterLogin(): void {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
      this.router.navigateByUrl(returnUrl);
      return;
    }

    const destination = this.authService.getCurrentUser()?.role === 'recruiter'
      ? '/recruitment'
      : '/assesments';
    this.router.navigateByUrl(destination);
  }
}
